import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Play, Sparkles, Wallet, LogOut, Loader2 } from "lucide-react";
import { stats } from "../data/mock";
import { useWallet } from "../context/WalletContext";
import ContractAddress from "./ContractAddress";

const Hero = () => {
  const { address, shortAddress, connect, disconnect, connecting, error, isConnected } = useWallet();
  const navigate = useNavigate();
  const [showError, setShowError] = useState(false);

  const handleConnect = async () => {
    setShowError(false);
    const pk = await connect();
    if (!pk) setShowError(true);
  };

  const handlePlay = () => navigate("/play");
  const handleGuest = () => navigate("/play?guest=1");

  return (
    <section className="relative pt-32 pb-24 overflow-hidden dotted-bg">
      <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-[#2a75bb]/20 blur-3xl" />
      <div className="absolute top-20 -right-32 w-96 h-96 rounded-full bg-[#ffcb05]/10 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
        <div className="min-w-0">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#ffcb05]/10 border border-[#ffcb05]/30 mb-6">
            <Sparkles size={14} className="text-[#ffcb05]" />
            <span className="text-xs text-[#ffcb05] tracking-wider mono-pixel" style={{ fontSize: 14 }}>
              SOLANA · ON-CHAIN TCG · PLAY-TO-EARN
            </span>
          </div>

          <h1 className="pixel-font text-[#ffcb05] glow-yellow leading-[1.15] text-2xl sm:text-4xl lg:text-5xl mb-6 break-words">
            ENTER THE<br />POKÉREALMS.
          </h1>
          <p className="text-white/70 text-base sm:text-lg max-w-xl leading-relaxed mb-6">
            Play PokéRealms to earn <span className="text-[#ffcb05]">on-chain TCG Pokémon cards</span> on Solana.
            Connect your Phantom wallet to start your journey — or jump in as a guest to explore.
          </p>

          {/* Contract Address (CA) */}
          <div className="mb-8" data-testid="hero-contract-address">
            <p className="mono-pixel text-white/40 tracking-widest mb-2" style={{ fontSize: 14 }}>
              CONTRACT ADDRESS (CA)
            </p>
            <ContractAddress />
          </div>

          {/* Wallet connection block */}
          <div className="mb-6">
            {!isConnected ? (
              <button
                onClick={handleConnect}
                disabled={connecting}
                className="group w-full sm:w-auto min-w-0 max-w-full inline-flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-6 py-4 rounded border-2 border-[#ab9ff2] bg-[#ab9ff2]/10 hover:bg-[#ab9ff2]/20 text-[#ab9ff2] transition-colors"
              >
                {connecting ? (
                  <Loader2 size={18} className="animate-spin shrink-0" />
                ) : (
                  <Wallet size={18} className="shrink-0" />
                )}
                <span className="pixel-font text-[10px] sm:text-xs tracking-wider truncate">
                  {connecting ? "CONNECTING…" : "CONNECT PHANTOM WALLET"}
                </span>
              </button>
            ) : (
              <div className="w-full sm:w-auto min-w-0 inline-flex items-center gap-3 px-5 py-4 rounded border-2 border-[#ab9ff2]/60 bg-[#ab9ff2]/10">
                <div className="w-8 h-8 rounded-full bg-[#ab9ff2]/30 flex items-center justify-center shrink-0">
                  <Wallet size={16} className="text-[#ab9ff2]" />
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase tracking-widest text-[#ab9ff2]/80 mono-pixel" style={{ fontSize: 12 }}>
                    CONNECTED
                  </span>
                  <span className="text-white font-mono text-sm" title={address}>
                    {shortAddress}
                  </span>
                </div>
                <button
                  onClick={disconnect}
                  title="Disconnect"
                  className="ml-3 w-9 h-9 rounded border border-white/10 hover:border-white/30 hover:bg-white/5 flex items-center justify-center text-white/60 hover:text-white"
                >
                  <LogOut size={14} />
                </button>
              </div>
            )}
            {showError && error && (
              <p className="text-sm text-red-400 mt-3">{error}</p>
            )}
          </div>

          {/* Play buttons */}
          <div className="flex flex-col items-start gap-3 mb-12">
            <button
              onClick={handlePlay}
              disabled={!isConnected}
              className={`press-start pixel-font text-xs sm:text-sm px-7 py-4 rounded inline-flex items-center justify-center gap-3 w-full sm:w-auto ${
                !isConnected ? "opacity-40 cursor-not-allowed" : ""
              }`}
              title={isConnected ? "Play with connected wallet" : "Connect wallet to play"}
            >
              <Play size={18} fill="currentColor" />
              PLAY NOW
            </button>
            <button
              onClick={handleGuest}
              className="text-white/60 hover:text-[#ffcb05] underline underline-offset-4 decoration-dotted text-sm transition-colors"
            >
              or play as guest →
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {stats.map((s, i) => (
              <div key={i} className="border border-white/10 rounded-lg px-4 py-3 bg-white/[0.02]">
                <div className="pixel-font text-[#ffcb05] text-lg">{s.value}</div>
                <div className="text-xs text-white/50 mt-1 uppercase tracking-wider">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Right — Pokeball illustration */}
        <div className="flex justify-center lg:justify-end">
          <div className="relative">
            <div className="absolute inset-0 -m-16 rounded-full bg-[#ee1515]/20 blur-3xl spin-slow" />
            <div className="pokeball float-y" />
            <div className="absolute inset-0 -m-10 rounded-full border border-dashed border-white/10 spin-slow" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
